const FITRO_CONTENT_IMAGE_PATH =
    /^\/api\/public\/partner-assets\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/content-images\/content-image-[a-f0-9]{32}\.(?:webp|png|jpg)$/i
const IMAGE_TAG = /<img\b[^>]*>/gi
const SOURCE_ATTRIBUTE = /\ssrc="([^"]*)"/i

function trustedApiBaseUrl(value: string | null | undefined): string | null {
    const trimmed = value?.trim().replace(/\/$/, '')
    if (!trimmed) return null
    try {
        const parsed = new URL(trimmed)
        if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password || parsed.search || parsed.hash) {
            return null
        }
        return `${parsed.origin}${parsed.pathname.replace(/\/$/, '')}`
    } catch {
        return null
    }
}

/** Rewrites only backend-sanitized Fitro image sources to the browser-visible API origin. */
export function resolveFitroContentImageSources(html: string, apiBaseUrl: string | null | undefined): string {
    if (!html) return html
    return html.replace(IMAGE_TAG, tag => tag.replace(SOURCE_ATTRIBUTE, (attribute, source: string) =>
        FITRO_CONTENT_IMAGE_PATH.test(source)
            ? attribute.replace(source, resolveFitroContentImageUrl(source, apiBaseUrl))
            : attribute
    ))
}

export function resolveFitroContentImageUrl(source: string, apiBaseUrl: string | null | undefined): string {
    if (!FITRO_CONTENT_IMAGE_PATH.test(source)) return ''
    const baseUrl = trustedApiBaseUrl(apiBaseUrl)
    return baseUrl ? `${baseUrl}${source}` : source
}

/** Visibility/excerpt only; input must already be sanitized by the backend. */
export function richContentText(html: string): string {
    return html.replace(/<!--[^]*?-->/g, '')
        .replace(/<(?:br|\/p|\/div|\/li|\/h[1-6])\b[^>]*>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&(?:nbsp|#160|#xa0);/gi, ' ')
        .replace(/&#(x[\da-f]+|\d+);/gi, (_, code: string) => {
            const n = code[0].toLowerCase() === 'x' ? parseInt(code.slice(1), 16) : Number(code)
            return n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : ''
        })
        .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"').replace(/&#39;/g, "'").trim()
}

export function hasRichContent(html: string | null | undefined): boolean {
    return Boolean(html && (richContentText(html).replace(/[\s\u200b\ufeff]/g, '') || /<img\b[^>]*\bsrc\s*=/i.test(html)))
}

export function resolveDescriptionContent(short?: string | null, full?: string | null) {
    const text = short?.trim() || null
    const html = hasRichContent(full) ? full!.trim() : null
    return {intro: text ? {kind: 'text' as const, value: text} : html ? {kind: 'html' as const, value: html} : null,
        additionalHtml: text && html ? html : null}
}

export function descriptionPreview(text: string, limit = 240): {text: string; truncated: boolean} {
    let prefix = ''
    let count = 0
    let truncated = false
    for (const {segment} of new Intl.Segmenter(undefined, {granularity: 'grapheme'}).segment(text)) {
        if (count++ >= limit) {truncated = true; break}
        prefix += segment
    }
    if (!truncated) return {text, truncated: false}
    const lastSpace = Math.max(prefix.lastIndexOf(' '), prefix.lastIndexOf('\n'))
    return {text: (lastSpace > prefix.length * 0.75 ? prefix.slice(0, lastSpace) : prefix).trimEnd() + '…', truncated: true}
}
